import { Fragment, type JSX, type ReactNode } from 'react';

import { KundeVerweis } from './Kontaktkarte';
import { Maskenverweis } from './Maskenverweis';
import type { Maske } from '../wegweiser';

/**
 * DIE WEGE ZWISCHEN DEN AKTEN (Auftrag CRM-118, L7; AUF-06, BUC-50).
 *
 * Die Belegkette Angebot → Auftrag → Rechnung soll in beide Richtungen zu
 * verfolgen sein, die Buchung als Ursprung eingeschlossen (AUF-06), und jede
 * Buchung hat ihre Kette zu Auftrag und Rechnung (BUC-50). Dazu kommt der Weg
 * zurück zur Kundenakte und zum Termin.
 *
 * EIN BÜNDEL STATT SIEBEN RÜCKRUFE. Die Arbeitsfläche setzt nur die Wege, deren
 * Zielmaske der Angemeldete sehen darf; ein fehlender Weg heißt „kein Recht",
 * und der Verweis bleibt Text (BEN-02). Ein Verweis, der in eine verweigerte
 * Maske führt, wäre eine Sackgasse.
 */
export type Aktenwege = {
  readonly kunde?: (id: string) => void;
  readonly angebot?: (id: string) => void;
  readonly auftrag?: (id: string) => void;
  readonly rahmen?: (id: string) => void;
  readonly rechnung?: (id: string) => void;
  readonly termin?: (id: string) => void;
  /** Die Buchungsakte trägt ihre Nummer in der Adresse, nicht die Kennung. */
  readonly buchung?: (nummer: string) => void;
};

export type Aktenziel = keyof Aktenwege;

/** Die Maske hinter einem Ziel — für die Adresse des Verweises. */
export function maskeZu(ziel: Aktenziel, id: string): Maske {
  switch (ziel) {
    case 'kunde':
      return { art: 'kundenakte', id };
    case 'angebot':
      return { art: 'angebotsakte', id };
    case 'auftrag':
      return { art: 'auftragsakte', id };
    case 'rahmen':
      return { art: 'rahmenakte', id };
    case 'rechnung':
      return { art: 'rechnungsakte', id };
    case 'termin':
      return { art: 'terminakte', id };
    case 'buchung':
      return { art: 'buchungsvorgang', nummer: id };
  }
}

/**
 * Ein Verweis auf eine verbundene Akte — oder Text, wo er nicht hinführen kann.
 *
 * KEIN VERWEIS OHNE KENNUNG. Vor CRM-118 stand im Hilfefeld `/kunden/undefined`:
 * Eine Maske ohne `id` ergab eine Adresse, die auf keine Akte zeigt. Hier wird
 * eine fehlende oder leere Kennung zu Text, statt einen toten Verweis zu bauen.
 */
export function Aktenverweis({
  ziel,
  id,
  wege,
  children,
}: {
  readonly ziel: Aktenziel;
  readonly id: string | null | undefined;
  readonly wege: Aktenwege | undefined;
  readonly children: ReactNode;
}): JSX.Element {
  const weg = wege?.[ziel];
  if (weg === undefined || id === null || id === undefined || id === '') return <>{children}</>;
  const verweis = (
    <Maskenverweis
      maske={maskeZu(ziel, id)}
      beiKlick={() => {
        weg(id);
      }}
    >
      {children}
    </Maskenverweis>
  );
  /* CRM-181: Am Kunden hängt die Kontaktkarte — am selben Recht wie der Weg. */
  return ziel === 'kunde' ? (
    <KundeVerweis kundeId={id} zurAkte={weg}>
      {verweis}
    </KundeVerweis>
  ) : (
    verweis
  );
}

/** Eine Zeile der Karte „Verbunden": eine Art von Akte und ihre Verweise. */
export type Verbindungszeile = {
  readonly beschriftung: string;
  readonly eintraege: readonly ReactNode[];
};

/**
 * DIE KARTE „VERBUNDEN" (CRM-118).
 *
 * Eine Beschreibungsliste wie die übrigen Angaben der Akte; sie bleibt auf dem
 * Telefon einspaltig (ÄA-63). Eine Zeile ohne Eintrag sagt „keine" und fällt
 * nicht weg: „Keine Rechnung" ist eine Auskunft, eine fehlende Zeile nicht.
 */
export function Verbunden({ zeilen }: { readonly zeilen: readonly Verbindungszeile[] }): JSX.Element {
  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Verbunden</h2>
        {/* AUF-06, BUC-50 */}
      </div>
      <dl className="angaben">
        {zeilen.map((z) => (
          <Fragment key={z.beschriftung}>
            <dt>{z.beschriftung}</dt>
            <dd>
              {z.eintraege.length === 0 ? (
                <span className="feld__hilfe">keine</span>
              ) : (
                <ul className="verbunden__liste">
                  {z.eintraege.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}
            </dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}
